import { useEffect, useMemo, useState } from 'react';
import {
  useLazyDataModel,
  params,
  arrayOf as arrayType,
  shape as shapeType,
  string as stringType,
  customType
} from '@thd-nucleus/data-sources';
import { QuickEstimateItem } from '../components/quick-estimate/QuickEstimateItem';

const BATCH_SIZE = 10;

export const useProductBatchLoader = ({
  items,
  storeId,
  loyaltyMembershipInput,
  skip
}) => {
  const [loadedItemIds, setLoadedItemIds] = useState([]);

  const [fetchProducts, productsResponse] = useLazyDataModel('products', {
    variables: {
      storeId,
      loyaltyMembershipInput,
      isBrandPricingPolicyCompliant: true
    }
  });

  const { data: productData, error: productsError, loading: productsLoading } = productsResponse || {};

  // Filter to get only catalog products (items with itemId that are not pure intents)
  const catalogProducts = useMemo(() => {
    return items?.filter((item) => item.itemId) || [];
  }, [items]);

  // Filter items that need product data fetching (not auto-resolved, has itemId)
  const itemIdsToFetch = useMemo(() => {
    const itemIds = catalogProducts.map((item) => item.itemId);
    return itemIds.filter((itemId) => !loadedItemIds.includes(itemId)).slice(0, BATCH_SIZE);
  }, [catalogProducts, loadedItemIds]);

  // Fetch products when needed
  useEffect(() => {
    const loadProduct = async () => {
      if (itemIdsToFetch.length > 0 && !productsLoading && !skip) {
        await fetchProducts({ variables: { itemIds: itemIdsToFetch } });
      }
    };
    loadProduct();
  }, [itemIdsToFetch, skip, productsLoading, fetchProducts]);

  // Update loaded item IDs after successful fetch
  useEffect(() => {
    if (productData && !productsError && itemIdsToFetch.length > 0) {
      setLoadedItemIds((prev) => {
        // Only add IDs that aren't already loaded to prevent infinite loops
        const newIds = itemIdsToFetch.filter((id) => !prev.includes(id));
        if (newIds.length === 0) {
          return prev;
        }
        return [...prev, ...newIds];
      });
    }
  }, [productData, productsError]);

  return {
    loadedItemIds,
    catalogProducts,
    productData,
    productsLoading,
    productsError
  };
};

useProductBatchLoader.dataModel = {
  products: params({
    itemIds: arrayType(stringType().isRequired()).isRequired(),
    dataSource: stringType(),
    loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
      svocID: stringType(),
      programTiers: arrayType(
        shapeType({
          tier: stringType(),
          program: stringType()
        })
      )
    })
  }).arrayOf(QuickEstimateItem.dataModel.product)
};
